import type { RawParams, Transition } from "@uirouter/react";
import startsWith from "lodash/startsWith";

import Sentry from "@similarweb/sentry-logger";

import {
    createKeywordGroupEntity,
    createAiTopicEntity,
    buildAiTopicKeywordListId,
} from "app/modules/demand-analysis/keyword-lists";
import { DEMAND_ANALYSIS_HOME_STATE_NAME } from "app/pages/market-research/shared/constants";
import { parseCountryParam, resolveAllowedCountryFromParam } from "app/pages/market-research/shared/countryResolution";
import { logWithTransitionScope } from "app/sentry/utils/logWithTransitionScope";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { KEYWORD_GROUP_PREFIX } from "app/services/keyword-groups";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";

const DEMAND_ANALYSIS_HOME_STATE = "digitalsuite_marketresearch_keywordmarketanalysis_home";

interface LegacyEntityParams extends RawParams {
    keywordGroup?: string | string[];
    searchTerm?: string | string[];
    topic?: string | string[];
    aiTopic?: string | string[];
    country?: string;
}

const dict: Record<string, string> = {
    "demand-search-trends": KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE,
};

function getSharedParams(params: RawParams): RawParams {
    return {
        country: params.country ?? WORLDWIDE_COUNTRY_ID,
        webSource: params.webSource,
        duration: params.duration,
        comparedDuration: params.comparedDuration,
        mtd: params.mtd,
    };
}

function getKeywordGroupParams(params: RawParams): RawParams {
    if (!params.keyword) {
        return {};
    }

    const sharedParams = getSharedParams(params);
    const keywordGroupIds: string[] = params.keyword
        .split(",")
        .map((id: string) => (startsWith(id, KEYWORD_GROUP_PREFIX) ? id.substring(1) : id));

    return {
        ...sharedParams,
        keywordGroup: keywordGroupIds,
    };
}

function getSearchTermsParams(params: RawParams): RawParams {
    if (!params.keyword) {
        return {};
    }

    const sharedParams = getSharedParams(params);

    return {
        ...sharedParams,
        searchTerm: params.keyword,
    };
}

export function redirectToSearchTrendsComparison(transition: Transition) {
    const params = transition.params();
    const toState = dict[params?.subpage];

    if (!toState) {
        return;
    }

    const toParams = params.isTerm === "true" ? getSearchTermsParams(params) : getKeywordGroupParams(params);

    logWithTransitionScope(
        transition,
        "redirectTo",
        `[Demand Analysis] Legacy Search Trends redirecting to ${toState}`,
        {
            redirectTargetStateName: toState,
            tags: { team: "ants" },
        },
    );

    return {
        state: toState,
        params: toParams,
    };
}

export function redirectFromLegacyEntityParams(transition: Transition) {
    const params = transition.params<LegacyEntityParams>();

    const hasLegacyParams = params.keywordGroup || params.searchTerm || params.topic || params.aiTopic;

    if (!hasLegacyParams) {
        return;
    }

    if (params.topic) {
        logWithTransitionScope(
            transition,
            "redirectTo",
            "[Demand Analysis] Redirecting from legacy topic param to home page",
            {
                redirectTargetStateName: DEMAND_ANALYSIS_HOME_STATE,
                tags: { team: "ants" },
            },
        );
        return {
            state: DEMAND_ANALYSIS_HOME_STATE,
            params: {},
        };
    }

    const ids: string[] = [];
    const parsedCountry = params?.country ? parseInt(params.country, 10) : WORLDWIDE_COUNTRY_ID;
    const country = Number.isNaN(parsedCountry) ? WORLDWIDE_COUNTRY_ID : parsedCountry;

    if (params.keywordGroup) {
        const keywordGroupIds = Array.isArray(params.keywordGroup) ? params.keywordGroup : [params.keywordGroup];
        keywordGroupIds.forEach((id: string) => {
            const cleanId = startsWith(id, KEYWORD_GROUP_PREFIX) ? id.substring(1) : id;
            const entity = createKeywordGroupEntity({ type: "KeywordGroup", keywordGroupId: cleanId }, cleanId);
            ids.push(entity.keywordListId);
        });
    }

    if (params.searchTerm) {
        const searchTerms = Array.isArray(params.searchTerm) ? params.searchTerm : [params.searchTerm];
        searchTerms.forEach((seed: string) => {
            ids.push(buildAiTopicKeywordListId(seed));
        });
    }

    if (params.aiTopic) {
        const aiTopics = Array.isArray(params.aiTopic) ? params.aiTopic : [params.aiTopic];
        aiTopics.forEach((seed: string) => {
            const entity = createAiTopicEntity({ type: "AiTopic", seed, country }, seed);
            ids.push(entity.keywordListId);
        });
    }

    const toParams = {
        ...params,
        id: ids,
        keywordGroup: undefined,
        searchTerm: undefined,
        topic: undefined,
        aiTopic: undefined,
    };

    logWithTransitionScope(
        transition,
        "redirectTo",
        "[Demand Analysis] Redirecting from legacy entity params to unified id param",
        {
            redirectTargetStateName: transition.to().name,
            tags: { team: "ants" },
        },
    );

    return {
        state: transition.to().name,
        params: toParams,
    };
}

interface UnifiedIdParams extends RawParams {
    id?: string | string[];
}

/**
 * Redirects legacy entity types (Topic, SearchTerm) from URL params:
 * - Topic;{id} -> Redirect to home page (predefined topics no longer supported)
 * - SearchTerm;{seed};{country} -> Convert to AiTopic;{seed};{country}
 */
export function redirectFromLegacyEntityTypes(transition: Transition) {
    const params = transition.params<UnifiedIdParams>();

    if (!params.id) {
        return;
    }

    const ids = Array.isArray(params.id) ? params.id : [params.id];

    // Check if any Topic entity is present - redirect to home
    const hasTopicEntity = ids.some((id) => id.startsWith("Topic;"));
    if (hasTopicEntity) {
        logWithTransitionScope(
            transition,
            "redirectTo",
            "[Demand Analysis] Redirecting from unsupported Topic entity to home page",
            {
                redirectTargetStateName: DEMAND_ANALYSIS_HOME_STATE,
                tags: { team: "ants" },
            },
        );
        return {
            state: DEMAND_ANALYSIS_HOME_STATE,
            params: {},
        };
    }

    // Check if any SearchTerm entity is present - convert to AiTopic
    const hasSearchTermEntity = ids.some((id) => id.startsWith("SearchTerm;"));
    if (hasSearchTermEntity) {
        const convertedIds = ids.map((id) => {
            if (!id.startsWith("SearchTerm;")) {
                return id;
            }

            const parts = id.split(";");
            if (parts.length !== 3) {
                return id;
            }

            const seed = parts[1];
            const country = parseInt(parts[2], 10);
            if (isNaN(country)) {
                return id;
            }

            return buildAiTopicKeywordListId(seed);
        });

        logWithTransitionScope(
            transition,
            "redirectTo",
            "[Demand Analysis] Converting SearchTerm entities to AiTopic",
            {
                redirectTargetStateName: transition.to().name,
                tags: { team: "ants" },
            },
        );

        return {
            state: transition.to().name,
            params: {
                ...params,
                id: convertedIds,
            },
        };
    }
}

interface DemandAnalysisHomeParams extends RawParams {
    country?: string | number;
}

export function ensureDemandAnalysisHomeCountryParam(transition: Transition) {
    const params = transition.params<DemandAnalysisHomeParams>();

    try {
        const resolvedCountry =
            resolveAllowedCountryFromParam(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, params.country) ??
            WORLDWIDE_COUNTRY_ID;
        const currentCountry = parseCountryParam(params.country);

        if (currentCountry === resolvedCountry) {
            return;
        }

        logWithTransitionScope(
            transition,
            "redirectTo",
            "[Demand Analysis] Setting missing/invalid home country state param",
            {
                redirectTargetStateName: DEMAND_ANALYSIS_HOME_STATE_NAME,
                tags: { team: "ants" },
            },
        );

        return {
            state: DEMAND_ANALYSIS_HOME_STATE_NAME,
            params: {
                ...params,
                country: resolvedCountry,
            },
        };
    } catch (error) {
        Sentry.error("[Demand Analysis] Failed to resolve home country state param", error as Error, {
            tags: { team: "ants" },
        });

        return {
            state: DEMAND_ANALYSIS_HOME_STATE_NAME,
            params: {
                ...params,
                country: WORLDWIDE_COUNTRY_ID,
            },
        };
    }
}
